import Link from "next/link";
import { useRef } from "react";
import { getGlobalData } from "utils/api";
import { CMSUrls } from "utils/cmsHelper";
import { getLocalizedPaths } from "utils/localize";

import Breadcrumb from "@/components/brandRefresh/common/Breadcrumb";
import { RefreshHeader } from "@/components/brandRefresh/common/PageTemplate";

export default function ThirdPartyAuthorization({ metadata }) {
  const guideRef = useRef(null);
  const powerRef = useRef(null);
  const courtRef = useRef(null);

  const scrollToSection = elementRef => {
    window.scrollTo({
      top: elementRef.current.offsetTop + 300,
      behavior: "smooth",
    });
  };

  return (
    <RefreshHeader metadata={metadata}>
      <div className="flex flex-col gap-spacing-12 lg:gap-spacing-14 text-sm lg:text-base 4xl:text-xl">
        <Breadcrumb />
        <div>
          <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
            Third party access to help manage your finances
          </h3>
          <p className="4xl:text-xl">
            At Onmo, we recognise that life events or changes in health may
            sometimes make it necessary for you to seek the assistance of a
            third party in managing your financial affairs. When you cannot
            manage your own finances, whether due to health, capacity, or other
            significant life events, a third party (someone not named on the
            account) may be granted access to operate your account.
          </p>
        </div>
        <div>
          <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
            Understanding third party access
          </h3>
          <p className="4xl:text-xl">
            There are several third party access options available to you:
          </p>
          <div className="flex justify-center gap-8 rounded-xl flex-col md:flex-row my-6">
            <div className="flex flex-col w-full bg-onmoPurple-50 p-6 h-[212px] rounded-xl">
              <h4 className="md:text-base font-medium mb-4">
                Third Party Mandate
              </h4>
              <p className="text-sm  leading-4 mb-3">
                {" "}
                Give someone you trust access to your account.
              </p>
              <p className="text-sm leading-4">
                Learn more including how to get set up here:
              </p>
              <p
                className="text-onmoPrimary mt-auto text-sm cursor-pointer"
                onClick={() => scrollToSection(guideRef)}
              >
                Third Party Mandate Guide
              </p>
            </div>

            <div className="flex flex-col w-full bg-onmoPurple-50 p-6 h-[212px] rounded-xl">
              <h4 className="md:text-base font-medium mb-4">
                Power of Attorney
              </h4>

              <p className="text-sm leading-4">
                If you need additional support over and above what a Third Party
                Mandate offers, this could be the option for you.
              </p>
              <p
                className="text-onmoPrimary mt-auto text-sm cursor-pointer"
                onClick={() => scrollToSection(powerRef)}
              >
                Power of Attorney Guide
              </p>
            </div>

            <div className="flex flex-col w-full bg-onmoPurple-50 p-6 h-[212px] rounded-xl">
              <h4 className="md:text-base font-medium mb-4">
                Court of Protection Order
              </h4>

              <p className="text-sm leading-4">
                If you want to support someone who isn’t able to make their own
                financial decision, you may consider a Court of Protection
                Order.
              </p>
              <p
                className="text-onmoPrimary mt-auto text-sm cursor-pointer"
                onClick={() => scrollToSection(courtRef)}
              >
                Court of Protection Order Guide
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <p className="text-onmoGrey text-xl 4xl:text-2xl" ref={guideRef}>
            GUIDE TO THIRD PARTY ACCESS
          </p>
          <div className="mt-6 ">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What is a Third Party mandate and how does it work?
            </h3>
            <p className="4xl:text-xl">
              A Third Party Mandate is a formal instruction from you informing
              us that you would like another party to support you with your
              account. You can appoint a trusted family member or friend to act
              as your third party representative.
            </p>
          </div>

          <div className="mt-6 4xl:text-xl">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What type of access will the third party have?
            </h3>
            <p className="4xl:text-xl">
              A third party will have basic access to your Account as follows:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>Obtain details about the account management only</li>
              <li>Be able to make repayments to the Account</li>
              <li>
                Set up a direct debit to support repayments to the Account
              </li>
            </ul>
            <p className="4xl:text-xl mt-6">
              They will not be able to do any of the following unless they have
              a Lasting Power of Attorney or Court of Protection Order:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>Open or close accounts in your name</li>
              <li>Provide updated personal details about you</li>
              <li>Obtain details about the Account</li>
              <li>Make payments of withdrawals</li>
              <li>Access or use the Onmo App</li>
              <li>Manage the account if you become mentally incapacitated</li>
            </ul>
          </div>

          <div className="mt-6 4xl:text-xl">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              How to set up a Third Party Mandate
            </h3>
            <p className="4xl:text-xl">
              To set up a Third Party Mandate, you’ll need:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>
                To have mental capacity to be able to make your own financial
                decisions.
              </li>
              <li>
                To give third party access to someone aged 18 years old or over,
                who is a UK resident and who is able to fulfil the
                identification requirements.
              </li>
            </ul>
            <p className="4xl:text-xl mt-6">
              Follow the steps below to set up a Third Party Mandate:{" "}
            </p>
            <ul className="list-decimal pl-6 mt-6">
              <li>
                Download the Third Party Mandate{" "}
                <Link
                  className="text-onmoPrimary"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={CMSUrls.thirdPartyForm}
                >
                  click here.
                </Link>
              </li>
              <li>
                You and the third party need to complete and sign the form; and
                ensure your signatures are verified.
              </li>
              <li>
                Send the completed form to us{" "}
                <Link
                  href="/contact-us"
                  target="_blank"
                  passHref
                  className="text-onmoPrimary"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div>
          <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
            Document Certification Standards
          </h3>
          <p>Certified documents must meet the following criteria:</p>
          <p className="mt-6 4xl:text-xl">
            Documents are clear, coloured photocopies certified by a Bank,
            Solicitor, Accountant, or Post Office or by one of the professions
            listed on{" "}
            <Link
              href="https://www.gov.uk/certifying-a-document"
              target="_blank"
              className="text-onmoPrimary"
            >
              www.gov.uk
            </Link>
          </p>

          <p className="mt-6 4xl:text-xl">
            Certified copies must be stamped to indicate they are ‘true copies
            of the original’. The certification must also include:
          </p>
          <ul className="list-disc pl-6 mt-6">
            <li>The name and address of the certifying organisation.</li>
            <li>The full name and signature of the certifying officer, and</li>
            <li>The date of certification</li>
          </ul>
        </div>

        <div>
          <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
            What happens after I send the Third Party Mandate
          </h3>
          <p>
            Upon receipt of the signed form, we will need to verify the third
            party’s identity and address by conducting electronic searches.
          </p>
          <p className="mt-6 4xl:text-xl">
            We will then send you and the third party confirmation that we have
            set up the Third Party Mandate and the third party will be able to
            support you with managing your account in line with the authorities
            of the Third Party Mandate.
          </p>
        </div>

        <div>
          <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
            How to cancel a Third Party Mandate
          </h3>
          <p className="4xl:text-xl">
            The Third Party Mandate can be cancelled due to the following
            reasons:
          </p>
          <ul className="list-disc pl-6 mt-6">
            <li>You or the third party die</li>
            <li>You or the third party lose mental capacity</li>
            <li>You or the third party want to cancel</li>
          </ul>
          <p className="mt-6 4xl:text-xl">
            Written requests may need to be supported by a certified copy of the
            death certificate or medical evidence and sent to us{" "}
            <Link
              href="/contact-us"
              target="_blank"
              passHref
              className="text-onmoPrimary"
            >
              Contact Us
            </Link>
          </p>
        </div>
        <div>
          <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
            What is a Power of Attorney?
          </h3>

          <p className="4xl:text-xl">
            A power of attorney is a legal document that appoints one or more
            people to make financial decisions for you, and to carry out
            everyday transactions on your behalf.
          </p>
          <p className="4xl:text-xl mt-6">
            There are 2 parties involved in a power of attorney:
          </p>
          <ul className="list-disc pl-6 mt-6">
            <li>
              The ‘donor’ is the person whose money, property or care is
              involved in the power of attorney.
            </li>
            <li>
              The ‘attorney’ is the person who has been tasked with helping to
              manage the money, property or care.
            </li>
          </ul>
          <p className="mt-6 4xl:text-xl">
            Written requests may need to be supported by a certified copy of the
            death certificate or medical evidence and sent to us{" "}
            <Link
              href="/contact-us"
              target="_blank"
              passHref
              className="text-onmoPrimary"
            >
              Contact Us
            </Link>
          </p>
        </div>
        <div>
          <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
            Which type of Power of Attorney do we accept?
          </h3>

          <p className="4xl:text-xl">
            For financial purposes, the Lasting Power of Attorney for Property
            and Financial Affairs is the relevant lasting power. If you have any
            other type of Power of Attorney, please contact us to see whether we
            can accept it.
          </p>
          <p className="4xl:text-xl mt-6">
            The Lasting Power of Attorney was introduced by the Mental Capacity
            Act 2005 and the act is supported by a code of practise and all
            attorneys under a lasting power are required to abide by it.
          </p>
          <p className="mt-6 4xl:text-xl">
            The Lasting Power of Attorney must be registered with the Office of
            the Public Guardian before it can be used. It therefore remains
            valid even if you lose mental capacity.
          </p>
        </div>

        <div>
          <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
            What can an attorney do?
          </h3>
          <p className="4xl:text-xl">
            An attorney will have full access to your Account just like you do.
            They can:
          </p>
          <ul className="list-disc pl-6 mt-6">
            <li>Open or close accounts in your name</li>
            <li>Provide updated personal details about you</li>
            <li>Obtain details about the Account and Account Management</li>
            <li>Make payments of withdrawals</li>
            <li>Access or use the Onmo App</li>
            <li>Be able to make repayments to the Account</li>
            <li>Set up a direct debit to support repayments to the Account</li>
            <li>Manage the account if you become mentally incapacitated</li>
          </ul>
        </div>
        <div className="flex flex-col gap-6">
          <p className="text-onmoGrey text-xl 4xl:text-2xl" ref={powerRef}>
            LASTING POWER OF ATTORNEY
          </p>
          <div className="mt-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              How to set up a Lasting Power of Attorney
            </h3>
            <p className="4xl:text-xl">
              To set up a Lasting Power of Attorney, you may want to contact a
              solicitor or View a lasting power of attorney -{" "}
              <Link
                href="https://www.gov.uk/view-lasting-power-of-attorney"
                target="_blank"
                className="text-onmoPrimary"
              >
                www.gov.uk
              </Link>
            </p>
          </div>
          <div className="mt-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Registering a Lasting Power of Attorney with us
            </h3>
            <p className="4xl:text-xl">
              To register your Lasting Power of Attorney with us, you’ll need to
              send the following to us:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>
                The original or certified copy of the Lasting Power of Attorney
                which must be registered and stamped by the Office of Public
                Guardian
              </li>
              <li>
                The Third Party Mandate which must be completed and signed by
                you and the attorney, and ensure your signatures are verified.
                Download the Third Party Mandate{" "}
                <Link
                  className="text-onmoPrimary"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={CMSUrls.thirdPartyForm}
                >
                  click here.
                </Link>
              </li>
            </ul>
            <p className="4xl:text-cl mt-6">
              The above documents should be sent to us{" "}
              <Link
                href="/contact-us"
                target="_blank"
                passHref
                className="text-onmoPrimary"
              >
                Contact Us
              </Link>
            </p>
            <p className="4xl:text-cl mt-6">
              If original documents are provided to Onmo, we will take
              photocopies for our records and return the originals via Recorded
              Delivery to the address provided by the sender of the documents.
            </p>
          </div>
          <div className="mt-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Why is a Third Party Mandate required if I am registering a
              Lasting Power of Attorney?
            </h3>
            <p className="4xl:text-xl">
              A Third Party Mandate will also need to be completed when
              registering a Lasting Power of Attorney with us. This is so we are
              able to collect the personal information of the Attorney and
              ensure they have agreed to our terms and conditions as well as our
              Privacy Policy. The Attorney will also need to sign the
              declaration confirming we can conduct electronic checks on them
              and that they agree to the conditions of the products they will be
              overseeing.
            </p>
          </div>
          <div className="mt-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What happens after I send the Lasting Power of Attorney & Third
              Party Mandate{" "}
            </h3>
            <p className="4xl:text-xl">Upon receipt we will need to verify:</p>
            <ul className="list-disc pl-6 mt-6">
              <li>
                That the Lasting Power of Attorney is still valid via checking
                with the Office of Public Guardian
              </li>
              <li>
                The attorney’s identify and address by conducting electronic
                searches.
              </li>
            </ul>
            <p className="4xl:text-cl mt-6">
              Once the above checks have been completed, we will send you and
              the attorney confirmation within 30 working days that we have set
              up the Lasting Power of Attorney and that the attorney will be
              able to support you with your financial affairs with us.
            </p>
          </div>
          <div className="mt-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              How to cancel a Lasting Power of Attorney
            </h3>
            <p className="4xl:text-xl">
              You can ask the Office of the Public Guardian (OPG) to remove an
              attorney if your lasting power of attorney (LPA) is registered and
              you still have mental capacity to make decisions.
            </p>
            <p className="4xl:text-xl mt-6">
              The cancellation confirmation from the OPG should then be sent to
              us so we can update our records and cancel the lasting power of
              attorney.
            </p>
          </div>
          <div className="mt-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Other reasons a Lasting Power of Attorney might end
            </h3>

            <p className="4xl:text-xl">
              A Lasting Power of Attorney will automatically end:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>When the donor dies</li>
              <li>If the attorney dies and there is no replacement attorney</li>
              <li>If the attorney loses the ability to make decisions</li>
              <li>
                If the attorney becomes bankrupt or subject to a Debt Relief
                Order.
              </li>
            </ul>
          </div>
          <div className="mt-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Document Certification Standards
            </h3>

            <p className="4xl:text-xl">
              Certified documents must meet the following criteria:
            </p>
            <p className="4xl:text-xl mt-6">
              Documents are clear, coloured photocopies certified by a Bank,
              Solicitor, Accountant, or Post Office or by one of the professions
              listed on{" "}
              <Link
                href="https://www.gov.uk/certifying-a-document"
                target="_blank"
                className="text-onmoPrimary"
              >
                www.gov.uk
              </Link>
            </p>
            <p className="4xl:text-xl mt-6">
              Certified copies must be stamped to indicate they are ‘true copies
              of the original’. The certification must also include:
            </p>
            <ul className="list-disc pl-6">
              <li>The name and address of the certifying organisation.</li>
              <li>
                The full name and signature of the certifying officer, and
              </li>
              <li>The date of certification</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <p className="text-onmoGrey text-xl 4xl:text-2xl" ref={courtRef}>
            COURT OF PROTECTION ORDER
          </p>
          <div className="mt-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What is a Court of Protection order?
            </h3>
            <p className="4xl:text-xl">
              The Court of Protection is a specialist court that makes decisions
              about the property, finances and personal welfare of those who
              lack the mental capacity to take decisions themselves.
            </p>
            <p className="4xl:text-xl mt-6">
              The Court of Protection Order is issued by the court and is a
              legal document appointing a ‘Deputy’ to manage the financial
              affairs of someone else.
            </p>
            <p className="4xl:text-xl mt-6">
              There are usually 2 parties involved in the Order:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>
                The ‘donor’ is the person whose money, property or care is
                involved in the Order
              </li>
              <li>
                The ‘deputy’ is the person appointed by the Court of Protection
                to deal with the property and financial affairs of a person who
                lacks the mental capacity to do themselves.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What can a deputy do?
            </h3>
            <p className="4xl:text-xl">
              As a deputy you will have full access to the donor’s Account. You
              can:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>Open or close accounts for the donor</li>
              <li>Provide updated personal details about the donor</li>
              <li>Obtain details about the Account and Account Management</li>
              <li>Make payments of withdrawals</li>
              <li>Access or use the Onmo App</li>
              <li>Be able to make repayments to the Account</li>
              <li>
                Set up a direct debit to support repayments to the Account
              </li>
            </ul>
          </div>
          <div className="my-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              How to set up a Court of Protection Order
            </h3>
            <p className="4xl:text-xl">
              To set up a Court of Protection Order, you may want to contact a
              contact a solicitor or visit Court of Protection{" "}
              <Link
                href="https://www.gov.uk/courts-tribunals/court-of-protection"
                target="_blank"
                className="text-onmoPrimary"
              >
                www.gov.uk
              </Link>
            </p>
          </div>
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Registering a Court of Protection Order with us
            </h3>
            <p className="4xl:text-xl">
              To register the Court of Protection Order with us, you’ll need to
              send the following to us:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>
                The original or certified copy of the Court of Protection Order
                which must be registered and stamped by the Court of Protection
              </li>
              <li>
                The Third Party Mandate which must be completed and signed by
                you (“the Deputy”), and ensure your signature is verified.
                Download the Third Party Mandate{" "}
                <Link
                  className="text-onmoPrimary"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={CMSUrls.thirdPartyForm}
                >
                  click here.
                </Link>
              </li>
            </ul>
            <p className="4xl:text-xl pt-6">
              The above documents should be sent to us via post{" "}
              <Link
                href="/contact-us"
                target="_blank"
                passHref
                className="text-onmoPrimary"
              >
                Contact Us
              </Link>
            </p>
            <p className="4xl:text-xl pt-6">
              If original documents are provided to us, we will take photocopies
              for our records and return the originals via Recorded Delivery to
              the address provided by the sender of the documents.
            </p>
          </div>
          <div className="my-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Why is a Third Party Mandate required if I am registering a Court
              of Protection Order?
            </h3>
            <p className="4xl:text-xl">
              A Third Party Mandate will also need to be completed when
              registering a Court of Protection Order with us. This is so we are
              able to collect your personal information as the Deputy, and
              ensure you have agreed to our terms and conditions as well as our
              Privacy Policy. You will also need to sign the declaration
              confirming we can conduct electronic checks on you and that you
              agree to the conditions of the products you will be overseeing.
            </p>
          </div>
          <div className="my-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What happens after I send the Court of Protection Order & Third
              Party Mandate
            </h3>
            <p className="4xl:text-xl">Upon receipt we will need to verify:</p>
            <ul className="list-disc pl-6 mt-6">
              <li>
                That the Court of Protection Order is valid via checking with
                the Court of Protection
              </li>
              <li>
                Your identity and address by conducting electronic searches.
              </li>
            </ul>
            <p className="4xl:text-xl pt-6">
              Once the above checks have been completed, we will send you
              confirmation within 30 working days that we have set up the Court
              of Protection Order and you as the Deputy will be able to support
              the donor with their financial affairs with us.
            </p>
          </div>
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              How to cancel a Court of Protection Order
            </h3>
            <p className="4xl:text-xl">
              The account holder/donor is unable to cancel the Court of
              Protection Order; the Order remains valid and the deputy retains
              full authority.
            </p>
            <p className="4xl:text-xl mt-6">
              You cannot stop being the deputy until you’ve made the correct
              application to the Court of Protection and received the relevant
              court order.
            </p>
          </div>
          <div className="my-6">
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Other reasons a Court of Protection Order might end
            </h3>
            <p className="4xl:text-xl">
              A Court of Protection Order can end for any of the following
              reasons, but before we can update our records, the updated court
              order must be sent to us:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>When the donor dies</li>
              <li>If the deputy dies and there is no replacement deputy</li>
              <li>If the deputy loses the ability to make decisions</li>
            </ul>
            <p className="4xl:text-xl mt-6">
              For more information please visit{" "}
              <Link
                className="text-onmoPrimary"
                passHref
                href="https://www.gov.uk/become-deputy/end-your-deputyship"
                target="_blank"
              >
                Deputies: make decisions for someone who lacks capacity: End
                your deputyship - GOV.UK
              </Link>
            </p>
          </div>
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Document Certification Standards
            </h3>
            <p className="4xl:text-xl">
              Certified documents must meet the following criteria:
            </p>
            <p className="4xl:text-xl mt-6">
              Documents are clear, coloured photocopies certified by a Bank,
              Solicitor, Accountant, or Post Office or by one of the professions
              listed on{" "}
              <Link
                href="https://www.gov.uk/certifying-a-document"
                target="_blank"
                className="text-onmoPrimary"
              >
                www.gov.uk
              </Link>
            </p>
            <p className="4xl:text-xl mt-6">
              Certified copies must be stamped to indicate they are ‘true copies
              of the original’. The certification must also include:
            </p>
            <ul className="list-disc pl-6 mt-6">
              <li>The name and address of the certifying organisation.</li>
              <li>
                The full name and signature of the certifying officer, and
              </li>
              <li>The date of certification</li>
            </ul>
          </div>
        </div>
      </div>
    </RefreshHeader>
  );
}

export async function getServerSideProps(context) {
  const { locale, locales, defaultLocale } = context;
  const globalLocale = await getGlobalData(locale);

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `Third Party Authorisation`,
  };

  return {
    props: {
      metadata,
      global: globalLocale.data,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}
